import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, Download, FolderOpen, Plus, Eye,
  ChevronDown, ChevronUp, Settings, ArrowLeft, Loader2, X,
  Edit2, Check, File, Image as ImageIcon, BookOpen, GripVertical
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ref-docs`;

const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: 'text-red-400' },
  'image/png': { icon: ImageIcon, color: 'text-blue-400' },
  'image/jpeg': { icon: ImageIcon, color: 'text-blue-400' },
  'image/gif': { icon: ImageIcon, color: 'text-blue-400' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: FileText, color: 'text-orange-400' },
  'application/vnd.ms-powerpoint': { icon: FileText, color: 'text-orange-400' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, color: 'text-blue-500' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileText, color: 'text-green-400' },
};

const getFileIcon = (contentType) => FILE_ICONS[contentType] || { icon: File, color: 'text-gray-400' };

const canPreview = (contentType) => {
  return contentType?.startsWith('image/') || contentType === 'application/pdf';
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ==================== PREVIEW PANEL ====================

const PreviewPanel = ({ file, onClose }) => {
  if (!file) return null;
  const previewUrl = `${API}/files/${file.id}/preview`;
  const isImage = file.content_type?.startsWith('image/');
  const isPdf = file.content_type === 'application/pdf';

  return (
    <div className="bg-white/[0.03] border border-white/10 p-4 rounded" data-testid="rd-preview-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-mono font-semibold text-gray-200 flex items-center gap-2">
          <Eye className="w-4 h-4 text-teal-400" />
          {file.label}
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[10px] text-gray-600 font-mono mb-3">{file.original_name} &middot; {formatSize(file.size)}</p>
      {isImage && (
        <img src={previewUrl} alt={file.label} className="w-full rounded border border-white/10" />
      )}
      {isPdf && (
        <iframe src={previewUrl} title={file.label} className="w-full h-[60vh] rounded border border-white/10" />
      )}
      {!isImage && !isPdf && (
        <div className="text-center py-8">
          <File className="w-10 h-10 text-gray-600 mx-auto mb-2" />
          <p className="text-xs text-gray-500 font-mono">Preview not available for this file type</p>
          <a href={`${API}/files/${file.id}/download`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-teal-400 hover:text-teal-300 text-xs font-mono">
            <Download className="w-3.5 h-3.5" /> Download to view
          </a>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================

export default function ReferenceDocuments({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("browse");
  const [sections, setSections] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Admin state
  const [newSectionName, setNewSectionName] = useState("");
  const [editingSection, setEditingSection] = useState(null);
  const [editSectionName, setEditSectionName] = useState("");
  const [uploadSection, setUploadSection] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadLabels, setUploadLabels] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Browse state
  const [expandedSections, setExpandedSections] = useState({});
  const [previewFile, setPreviewFile] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [secRes, fileRes] = await Promise.all([
        axios.get(`${API}/sections`),
        axios.get(`${API}/files`)
      ]);
      setSections(secRes.data);
      setFiles(fileRes.data);
      if (secRes.data.length > 0 && !uploadSection) {
        setUploadSection(secRes.data[0].id);
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [uploadSection]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!user?.email) return;
    axios.get(`${API}/admins`).then(res => {
      const emails = (res.data || []).map(a => a.email.toLowerCase());
      setIsAdmin(emails.includes(user.email.toLowerCase()));
    }).catch(() => {});
  }, [user]);

  // Section CRUD
  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;
    try {
      await axios.post(`${API}/sections`, { name: newSectionName.trim() });
      setNewSectionName("");
      toast.success("Section added");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleRenameSection = async (id) => {
    if (!editSectionName.trim()) return;
    try {
      await axios.put(`${API}/sections/${id}`, { name: editSectionName.trim() });
      setEditingSection(null);
      toast.success("Section renamed");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm("Delete this section and all its files?")) return;
    try {
      await axios.delete(`${API}/sections/${id}`);
      toast.success("Section deleted");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // File Upload
  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    const newLabels = { ...uploadLabels };
    newFiles.forEach(f => { if (!newLabels[f.name]) newLabels[f.name] = f.name.replace(/\.[^.]+$/, ''); });
    setUploadFiles(prev => [...prev, ...newFiles]);
    setUploadLabels(newLabels);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0 || !uploadSection) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('label', uploadLabels[file.name] || file.name.replace(/\.[^.]+$/, ''));
        fd.append('section_id', uploadSection);
        await axios.post(`${API}/files`, fd);
      }
      toast.success(`${uploadFiles.length} file(s) uploaded`);
      setUploadFiles([]);
      setUploadLabels({});
      fetchData();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleDeleteFile = async (id) => {
    try {
      await axios.delete(`${API}/files/${id}`);
      toast.success("File deleted");
      if (previewFile?.id === id) setPreviewFile(null);
      fetchData();
    } catch { toast.error("Delete failed"); }
  };

  const toggleSection = (id) => setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  const getFilesBySection = (sectionId) => files.filter(f => f.section_id === sectionId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d1520" }}>
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0d1520" }} data-testid="ref-docs-page">
      <header className="border-b border-white/10 sticky top-0 z-30" style={{ background: "#0d1520" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-gray-400 hover:text-white" data-testid="rd-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-teal-400" />
              <h1 className="text-lg font-mono font-semibold text-white">CME Reference Documents</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
            <span className="text-xs text-gray-400 hidden sm:inline">{user?.name || user?.email}</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-[#0a0a0c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1">
          {[
            { id: "browse", label: "Browse Documents", icon: BookOpen },
            ...(isAdmin ? [{ id: "admin", label: "Admin", icon: Settings }] : [])
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`rd-tab-${tab.id}`}
              className={`px-5 py-3 font-mono text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors
                ${activeTab === tab.id ? "border-teal-500 text-teal-400" : "border-transparent text-gray-500 hover:text-gray-300"}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* ==================== BROWSE TAB ==================== */}
        {activeTab === "browse" && (
          <div data-testid="rd-browse-section">
            {files.length === 0 ? (
              <div className="bg-white/[0.03] border border-white/10 p-12 text-center rounded">
                <BookOpen className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 font-mono text-sm">No documents uploaded yet.</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-12 gap-6">
                {/* Left: Sections & Files */}
                <div className="lg:col-span-5 space-y-3">
                  {sections.map(section => {
                    const sFiles = getFilesBySection(section.id);
                    if (sFiles.length === 0) return null;
                    const expanded = expandedSections[section.id] === true;
                    return (
                      <div key={section.id} className="bg-white/[0.03] border border-white/10 rounded" data-testid={`rd-section-${section.id}`}>
                        <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
                          onClick={() => toggleSection(section.id)}>
                          <h3 className="text-sm font-mono text-gray-200 flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-teal-400" /> {section.name}
                            <span className="text-xs text-gray-600">({sFiles.length})</span>
                          </h3>
                          {expanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                        </button>
                        {expanded && (
                          <div className="px-3 pb-3 space-y-1.5">
                            {sFiles.map(f => {
                              const { icon: Icon, color } = getFileIcon(f.content_type);
                              const isPreviewing = previewFile?.id === f.id;
                              return (
                                <div key={f.id}
                                  className={`flex items-center gap-3 px-3 py-2.5 border rounded transition-all
                                    ${isPreviewing ? "border-teal-500/50 bg-teal-500/10 ring-1 ring-teal-400/40" : "border-white/5 hover:border-white/15"}`}
                                  data-testid={`rd-file-${f.id}`}
                                >
                                  <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-mono text-gray-200 truncate">{f.label}</div>
                                    <div className="text-[10px] text-gray-600 truncate">{f.original_name} &middot; {formatSize(f.size)}</div>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    {canPreview(f.content_type) && (
                                      <button onClick={() => setPreviewFile(isPreviewing ? null : f)}
                                        className={`p-1 rounded ${isPreviewing ? 'text-teal-400' : 'text-gray-600 hover:text-teal-400'}`}
                                        data-testid={`rd-preview-${f.id}`} title="Preview">
                                        <Eye className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    <a href={`${API}/files/${f.id}/download`} target="_blank" rel="noreferrer"
                                      className="p-1 rounded text-gray-600 hover:text-blue-400" title="Download"
                                      data-testid={`rd-download-${f.id}`}>
                                      <Download className="w-3.5 h-3.5" />
                                    </a>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Right: Preview */}
                <div className="lg:col-span-7">
                  {previewFile ? (
                    <div className="sticky top-16">
                      <PreviewPanel file={previewFile} onClose={() => setPreviewFile(null)} />
                    </div>
                  ) : (
                    <div className="bg-white/[0.02] border border-white/5 rounded p-8 text-center sticky top-16">
                      <Eye className="w-8 h-8 text-gray-700 mx-auto mb-3" />
                      <p className="text-xs text-gray-600 font-mono">Select a file to preview (images & PDFs)</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ==================== ADMIN TAB ==================== */}
        {activeTab === "admin" && isAdmin && (
          <div className="space-y-5" data-testid="rd-admin-section">
            {/* Add Section */}
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-400" /> Add Section
              </h3>
              <div className="flex gap-3">
                <input type="text" value={newSectionName} onChange={e => setNewSectionName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddSection()} placeholder="Section name"
                  className="flex-1 bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200 rounded focus:outline-none focus:border-teal-500/50 placeholder-gray-600"
                  data-testid="rd-new-section-input" />
                <button onClick={handleAddSection}
                  className="bg-teal-600 hover:bg-teal-500 text-white px-5 py-2 font-mono text-sm rounded"
                  data-testid="rd-add-section-btn">Add</button>
              </div>
            </div>

            {/* Sections List */}
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-blue-400" /> Sections
              </h3>
              {sections.length === 0 ? (
                <p className="text-xs text-gray-600 font-mono">No sections yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {sections.map(s => (
                    <div key={s.id} className="flex items-center justify-between px-3 py-2.5 border border-white/5 rounded">
                      {editingSection === s.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input type="text" value={editSectionName} onChange={e => setEditSectionName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleRenameSection(s.id)} autoFocus
                            className="flex-1 bg-white/5 border border-white/10 px-2 py-1 text-sm font-mono text-gray-200 rounded focus:outline-none focus:border-teal-500/50" />
                          <button onClick={() => handleRenameSection(s.id)} className="text-emerald-400 hover:text-emerald-300"><Check className="w-4 h-4" /></button>
                          <button onClick={() => setEditingSection(null)} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <GripVertical className="w-4 h-4 text-gray-700" />
                            <span className="text-sm font-mono text-gray-200">{s.name}</span>
                            <span className="text-xs text-gray-600 font-mono">({getFilesBySection(s.id).length})</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => { setEditingSection(s.id); setEditSectionName(s.name); }} className="text-gray-600 hover:text-teal-400 p-1"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleDeleteSection(s.id)} className="text-gray-600 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Files */}
            <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <Upload className="w-4 h-4 text-amber-400" /> Upload Documents
              </h3>
              {sections.length === 0 ? (
                <p className="text-xs text-gray-500 font-mono">Create a section first.</p>
              ) : (
                <>
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 font-mono block mb-1">Target Section</label>
                    <select value={uploadSection} onChange={e => setUploadSection(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200 rounded focus:outline-none focus:border-teal-500/50"
                      data-testid="rd-section-select">
                      {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} data-testid="rd-file-input" />
                  <div onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-white/10 hover:border-teal-500/30 p-8 text-center cursor-pointer transition-colors rounded"
                    data-testid="rd-upload-zone">
                    <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-sm text-gray-400 font-mono"><span className="text-teal-400">Click to upload</span> or drag and drop</p>
                    <p className="text-xs text-gray-600 font-mono mt-1">Any file format — PDF, PPTX, images, Excel, Word, etc.</p>
                  </div>
                  {uploadFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {uploadFiles.map(file => (
                        <div key={file.name} className="flex items-center gap-3 border border-white/5 px-3 py-2 rounded">
                          <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                          <span className="text-xs text-gray-400 font-mono flex-shrink-0 truncate max-w-[120px]">{file.name}</span>
                          <input type="text" placeholder="Label" value={uploadLabels[file.name] || ''}
                            onChange={e => setUploadLabels({ ...uploadLabels, [file.name]: e.target.value })}
                            className="flex-1 bg-white/5 border border-white/10 px-2 py-1 text-xs font-mono text-gray-200 rounded focus:outline-none focus:border-teal-500/50" />
                          <button onClick={() => setUploadFiles(uploadFiles.filter(f => f.name !== file.name))} className="text-gray-600 hover:text-red-400"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                      <button onClick={handleUpload} disabled={uploading}
                        className="w-full py-2.5 bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white font-mono text-sm flex items-center justify-center gap-2 mt-3 rounded"
                        data-testid="rd-upload-btn">
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {uploading ? "Uploading..." : `Upload ${uploadFiles.length} File(s)`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* File Library */}
            {files.length > 0 && (
              <div className="bg-white/[0.03] border border-white/10 p-5 rounded">
                <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" /> File Library
                </h3>
                {sections.map(section => {
                  const sFiles = getFilesBySection(section.id);
                  if (sFiles.length === 0) return null;
                  return (
                    <div key={section.id} className="mb-4">
                      <div className="text-xs text-gray-500 font-mono mb-2">{section.name} — {sFiles.length} file(s)</div>
                      <div className="space-y-1">
                        {sFiles.map(f => {
                          const { icon: Icon, color } = getFileIcon(f.content_type);
                          return (
                            <div key={f.id} className="flex items-center gap-3 px-3 py-2 border border-white/5 rounded hover:border-white/10">
                              <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-mono text-gray-200 truncate">{f.label}</div>
                                <div className="text-[10px] text-gray-600 truncate">{f.original_name} &middot; {formatSize(f.size)}</div>
                              </div>
                              <button onClick={() => handleDeleteFile(f.id)} className="text-gray-600 hover:text-red-400 p-1">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
